var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from 'react-bootstrap';
import Slider from 'rc-slider';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import { useImageFilter } from 'cc-editor/components/hooks/useImageFilter';
// Calculates the initial crop rectangle matching the minimum dimensions in the DOM
function createMinSizeCrop(mediaWidth, mediaHeight, naturalWidth, naturalHeight, minBlockWidth, minBlockHeight, aspect) {
    if (!naturalWidth || !naturalHeight || !mediaWidth || !mediaHeight) {
        return { unit: '%', x: 5, y: 5, width: 90, height: 90 };
    }
    // Calculate the target minimum width/height in display pixels
    const targetDomWidth = (minBlockWidth / naturalWidth) * mediaWidth;
    const targetDomHeight = (minBlockHeight / naturalHeight) * mediaHeight;
    // If the image display size is smaller than required minimum resolution, expand to 100%
    if (targetDomWidth >= mediaWidth || targetDomHeight >= mediaHeight) {
        if (aspect) {
            return centerCrop(makeAspectCrop({ unit: '%', width: 100 }, aspect, mediaWidth, mediaHeight), mediaWidth, mediaHeight);
        }
        return { unit: '%', x: 0, y: 0, width: 100, height: 100 };
    }
    const widthPct = (targetDomWidth / mediaWidth) * 100;
    if (aspect) {
        return centerCrop(makeAspectCrop({ unit: '%', width: widthPct }, aspect, mediaWidth, mediaHeight), mediaWidth, mediaHeight);
    }
    const heightPct = (targetDomHeight / mediaHeight) * 100;
    return {
        unit: '%',
        x: (100 - widthPct) / 2,
        y: (100 - heightPct) / 2,
        width: widthPct,
        height: heightPct,
    };
}
const ImageEditorFTs = ({ editorImageSrc, scaleFree, freeScale, imgBlockWidth, imgBlockHeight, imgWidth, imgHeight, imageUpload, setCropperScaleFree, resetCropperImage, messages, disabled = false, onImageLoaded, handleIEclose, }) => {
    var _a, _b, _c, _d, _e;
    const imgRef = useRef(null);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);
    const [flipX, setFlipX] = useState(false);
    const [flipY, setFlipY] = useState(false);
    const [showMoreSettings, setShowMoreSettings] = useState(false);
    const [isBelowMinResolution, setIsBelowMinResolution] = useState(false);
    const [currentCroppedSize, setCurrentCroppedSize] = useState({
        width: 0,
        height: 0,
    });
    const safeImgBlockWidth = imgBlockWidth > 0 ? imgBlockWidth : 1;
    const safeImgBlockHeight = imgBlockHeight > 0 ? imgBlockHeight : 1;
    const customRatio = safeImgBlockWidth / safeImgBlockHeight;
    const aspect = scaleFree ? undefined : customRatio;
    const src = typeof editorImageSrc === 'string'
        ? editorImageSrc
        : editorImageSrc && typeof editorImageSrc.src === 'string'
            ? editorImageSrc.src
            : '';
    const { filterAsString, currentFilterData, renderSlider, options, setFilterSlider, resetFilters } = useImageFilter(messages);
    const onImageLoad = (e) => {
        const img = e.currentTarget;
        const initialCrop = createMinSizeCrop(img.width, img.height, img.naturalWidth, img.naturalHeight, safeImgBlockWidth, safeImgBlockHeight, aspect);
        setCrop(initialCrop);
        setCompletedCrop(convertToPixelCrop(initialCrop, img.width, img.height));
        if (onImageLoaded) {
            onImageLoaded();
        }
    };
    // Initialization / Reset when toggling scale mode
    const prevScaleFreeRef = useRef(scaleFree);
    useEffect(() => {
        if (prevScaleFreeRef.current !== scaleFree && imgRef.current && imgRef.current.width > 0) {
            prevScaleFreeRef.current = scaleFree;
            const img = imgRef.current;
            const newCrop = createMinSizeCrop(img.width, img.height, img.naturalWidth, img.naturalHeight, safeImgBlockWidth, safeImgBlockHeight, aspect);
            setCrop(newCrop);
            setCompletedCrop(convertToPixelCrop(newCrop, img.width, img.height));
        }
    }, [scaleFree, aspect, safeImgBlockWidth, safeImgBlockHeight]);
    // Check output resolution on crop selection changes AND zoom scaling
    useEffect(() => {
        if (completedCrop && completedCrop.width > 0 && completedCrop.height > 0 && imgRef.current) {
            const image = imgRef.current;
            const naturalW = image.naturalWidth;
            const naturalH = image.naturalHeight;
            if (!naturalW || !naturalH || !image.width || !image.height)
                return;
            // Ratio between DOM image size and original source resolution
            const baseScaleX = naturalW / image.width;
            const baseScaleY = naturalH / image.height;
            // Zooming in (scale > 1) reduces effective original pixels captured
            const currentScale = scale || 1;
            const effectiveWidth = Math.round((completedCrop.width * baseScaleX) / currentScale);
            const effectiveHeight = Math.round((completedCrop.height * baseScaleY) / currentScale);
            setCurrentCroppedSize({ width: effectiveWidth, height: effectiveHeight });
            // 2px tolerance buffer for floating-point rounding errors
            const isTooSmall = effectiveWidth < safeImgBlockWidth - 2 || effectiveHeight < safeImgBlockHeight - 2;
            setIsBelowMinResolution(isTooSmall);
        }
    }, [completedCrop, safeImgBlockWidth, safeImgBlockHeight, scale]);
    const handleReset = () => {
        setScale(1);
        setRotate(0);
        setFlipX(false);
        setFlipY(false);
        resetCropperImage();
        if (imgRef.current) {
            const img = imgRef.current;
            const initialCrop = createMinSizeCrop(img.width, img.height, img.naturalWidth, img.naturalHeight, safeImgBlockWidth, safeImgBlockHeight, aspect);
            setCrop(initialCrop);
            setCompletedCrop(convertToPixelCrop(initialCrop, img.width, img.height));
        }
    };
    const generateProcessedCanvas = (image, cropX, cropY, cropW, cropH, boundingWidth, boundingHeight, rad, flipX, flipY, filterAsString) => {
        // 1. ROTATE
        const rotatedCanvas = document.createElement('canvas');
        rotatedCanvas.width = boundingWidth;
        rotatedCanvas.height = boundingHeight;
        const rotatedCtx = rotatedCanvas.getContext('2d');
        if (!rotatedCtx)
            return null;
        rotatedCtx.fillStyle = '#ffffff';
        rotatedCtx.fillRect(0, 0, boundingWidth, boundingHeight);
        rotatedCtx.filter = filterAsString;
        rotatedCtx.save();
        rotatedCtx.translate(boundingWidth / 2, boundingHeight / 2);
        rotatedCtx.rotate(rad);
        rotatedCtx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2, image.naturalWidth, image.naturalHeight);
        rotatedCtx.restore();
        // 2. CROP
        const canvas = document.createElement('canvas');
        canvas.width = cropW;
        canvas.height = cropH;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return null;
        ctx.drawImage(rotatedCanvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
        // 3. FLIP
        if (!flipX && !flipY) {
            return canvas;
        }
        const flippedCanvas = document.createElement('canvas');
        flippedCanvas.width = cropW;
        flippedCanvas.height = cropH;
        const flippedCtx = flippedCanvas.getContext('2d');
        if (!flippedCtx)
            return null;
        flippedCtx.save();
        flippedCtx.translate(flipX ? cropW : 0, flipY ? cropH : 0);
        flippedCtx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
        flippedCtx.drawImage(canvas, 0, 0, cropW, cropH);
        flippedCtx.restore();
        return flippedCanvas;
    };
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!imgRef.current || !completedCrop)
            return;
        const image = imgRef.current;
        const naturalWidth = image.naturalWidth;
        const naturalHeight = image.naturalHeight;
        if (!naturalWidth || !naturalHeight)
            return;
        // 1. Normalize rotation angle (0 - 360 degrees)
        const normRotate = ((rotate % 360) + 360) % 360;
        const rad = (normRotate * Math.PI) / 180;
        // 2. Calculate bounding box envelope of the rotated image (matching Imagine backend logic)
        const boundingWidth = Math.round(Math.abs(naturalWidth * Math.cos(rad)) + Math.abs(naturalHeight * Math.sin(rad)));
        const boundingHeight = Math.round(Math.abs(naturalWidth * Math.sin(rad)) + Math.abs(naturalHeight * Math.cos(rad)));
        // 3. Determine screen/DOM bounding dimensions
        const imgRect = image.getBoundingClientRect();
        const containerEl = image.closest('.ReactCrop') || image.parentElement;
        const containerRect = containerEl ? containerEl.getBoundingClientRect() : imgRect;
        const cropDOMWidth = image.width || 1;
        const cropDOMHeight = image.height || 1;
        const selScreenX = containerRect.left + (completedCrop.x / cropDOMWidth) * containerRect.width;
        const selScreenY = containerRect.top + (completedCrop.y / cropDOMHeight) * containerRect.height;
        const selScreenWidth = (completedCrop.width / cropDOMWidth) * containerRect.width;
        const selScreenHeight = (completedCrop.height / cropDOMHeight) * containerRect.height;
        // Relative position from top-left corner of transformed image
        const relX = selScreenX - imgRect.left;
        const relY = selScreenY - imgRect.top;
        const scaleFactorX = imgRect.width > 0 ? boundingWidth / imgRect.width : 1;
        const scaleFactorY = imgRect.height > 0 ? boundingHeight / imgRect.height : 1;
        let cropW = Math.round(selScreenWidth * scaleFactorX);
        let cropH = Math.round(selScreenHeight * scaleFactorY);
        let cropX = Math.round(relX * scaleFactorX);
        let cropY = Math.round(relY * scaleFactorY);
        // 4. FLIP CORRECTION: Revert coordinates across bounding box if flipped in UI
        if (flipX) {
            cropX = boundingWidth - cropX - cropW;
        }
        if (flipY) {
            cropY = boundingHeight - cropY - cropH;
        }
        // 5. Edge overflow protection
        cropX = Math.max(0, Math.min(cropX, boundingWidth - 1));
        cropY = Math.max(0, Math.min(cropY, boundingHeight - 1));
        cropW = Math.max(1, Math.min(cropW, boundingWidth - cropX));
        cropH = Math.max(1, Math.min(cropH, boundingHeight - cropY));
        const cropData = {
            x: cropX,
            y: cropY,
            width: cropW,
            height: cropH,
            rotate: normRotate,
            scaleX: flipX ? -1 : 1, // Represents flip direction only (-1 or 1)
            scaleY: flipY ? -1 : 1,
            minWidth: safeImgBlockWidth,
            minHeight: safeImgBlockHeight,
        };
        /*
         * The canvas now represents exactly:
         *
         *   rotate -> crop -> flip
         *
         * while cropData contains the same coordinates sent to
         * the backend.
         */
        const canvas = generateProcessedCanvas(image, cropX, cropY, cropW, cropH, boundingWidth, boundingHeight, rad, flipX, flipY, filterAsString);
        if (!canvas)
            return;
        imageUpload(canvas, cropData, isBelowMinResolution, filterAsString);
    });
    return (_jsxs(_Fragment, { children: [_jsxs("div", { style: {
                    height: '55vh',
                    width: '100%',
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    // Checkerboard pattern background for transparent image areas
                    backgroundColor: '#e6e6e6',
                    backgroundImage: `
            linear-gradient(45deg, #cccccc 25%, transparent 25%),
            linear-gradient(-45deg, #cccccc 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #cccccc 75%),
            linear-gradient(-45deg, transparent 75%, #cccccc 75%)
          `,
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                }, children: [isBelowMinResolution && (_jsxs("div", { style: {
                            position: 'absolute',
                            top: '12px',
                            zIndex: 100,
                            backgroundColor: '#fff3cd',
                            color: '#000000',
                            border: '1px solid #fff3cd',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12px',
                            fontWeight: 500,
                            pointerEvents: 'none',
                            margin: '3px',
                        }, children: [_jsx("i", { className: "fad fa-exclamation-triangle" }), _jsxs("span", { children: [(_a = messages === null || messages === void 0 ? void 0 : messages.editor) === null || _a === void 0 ? void 0 : _a.resolution_too_low, " (", currentCroppedSize.width, " x ", currentCroppedSize.height, "px) \u2014 Min:", ' ', safeImgBlockWidth, " x ", safeImgBlockHeight, "px. ", (_b = messages === null || messages === void 0 ? void 0 : messages.editor) === null || _b === void 0 ? void 0 : _b.blurry_output] })] })), !!src && (_jsx(ReactCrop, { crop: crop, onChange: (c) => setCrop(c), onComplete: (c) => setCompletedCrop(c), keepSelection: true, aspect: aspect, style: { maxHeight: '100%' }, children: _jsx("img", { ref: imgRef, src: src, alt: "Crop Target", crossOrigin: "anonymous", onLoad: onImageLoad, style: {
                                maxHeight: '50vh',
                                maxWidth: '100%',
                                transform: `scale(${flipX ? -scale : scale}, ${flipY ? -scale : scale}) rotate(${rotate}deg)`,
                                filter: filterAsString,
                            } }) }))] }), _jsxs("div", { style: {
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '3px',
                    flexWrap: 'wrap',
                }, children: [_jsx(Button, { variant: "light", size: "lg", style: { zIndex: 101 }, onClick: handleReset, disabled: disabled, title: messages.editor.reset, children: _jsx("i", { className: "fad fa-eraser" }) }), _jsx(Button, { variant: "light", size: "lg", onClick: () => setRotate((prev) => prev - 90), disabled: disabled, title: messages.editor.rotation_left, children: _jsx("i", { className: "fad fa-undo-alt" }) }), _jsx(Button, { variant: "light", size: "lg", onClick: () => setRotate((prev) => prev + 90), disabled: disabled, title: messages.editor.rotation_right, children: _jsx("i", { className: "fad fa-redo-alt" }) }), _jsx(Button, { variant: "light", size: "lg", style: { zIndex: 101 }, onClick: () => setFlipX((prev) => !prev), disabled: disabled, title: messages.editor.mirror_x, children: _jsx("i", { className: "fad fa-exchange-alt" }) }), _jsx(Button, { variant: "light", size: "lg", style: { zIndex: 101 }, onClick: () => setFlipY((prev) => !prev), disabled: disabled, title: messages.editor.mirror_y, children: _jsx("i", { className: "fad fa-sort-alt" }) }), freeScale ? (_jsx(Button, { size: "lg", onClick: () => setCropperScaleFree(!scaleFree), className: "scale-free", disabled: disabled, "data-freescale": scaleFree ? 'true' : 'false', title: messages.editor.free_scale, children: !scaleFree ? (_jsx("i", { className: "fa-thin fa-expand", title: messages.editor.set_scale_free })) : (_jsx("i", { className: "fa-thin fa-expand-wide", title: messages.editor.unset_scale_free })) })) : null, _jsx(Button, { variant: "light", size: "lg", onClick: () => setShowMoreSettings(!showMoreSettings), disabled: disabled, title: messages.editor.more_settings, children: _jsx("i", { className: "fa-duotone fa-solid fa-sliders" }) }), _jsxs(Button, { variant: "success", size: "lg", style: { zIndex: 101 }, onClick: handleSave, disabled: disabled, children: [_jsx("i", { className: "fal fa-save" }), ' ' + ((_d = (_c = messages === null || messages === void 0 ? void 0 : messages.editor) === null || _c === void 0 ? void 0 : _c.save) !== null && _d !== void 0 ? _d : 'Save')] }), _jsx(Button, { variant: "danger", size: "lg", style: { zIndex: 101 }, onClick: handleIEclose, children: messages.editor.abort })] }), showMoreSettings && (_jsxs("div", { className: "image-editor-more-settings", style: {}, children: [_jsxs("div", { className: "image-editor-sliders", style: {}, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx("i", { className: "fad fa-search-plus", style: { width: '20px', textAlign: 'center' }, title: "Zoom" }), _jsx("div", { style: { flex: 1 }, children: _jsx(Slider, { step: 0.01, min: scaleFree ? 0.1 : 1, max: 3, value: scale, onChange: (v) => setScale(Array.isArray(v) ? v[0] : v) }) }), _jsxs("span", { style: { minWidth: '45px', fontSize: '12px', textAlign: 'right', fontWeight: 'bold' }, children: [Math.round(scale * 100), "%"] })] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx("i", { className: "fad fa-sync-alt", style: { width: '20px', textAlign: 'center' }, title: (_e = messages === null || messages === void 0 ? void 0 : messages.editor) === null || _e === void 0 ? void 0 : _e.rotation_in_degree }), _jsx("div", { style: { flex: 1 }, children: _jsx(Slider, { step: 1, min: -180, max: 180, value: rotate, onChange: (v) => setRotate(Array.isArray(v) ? v[0] : v) }) }), _jsxs("span", { style: { minWidth: '45px', fontSize: '12px', textAlign: 'right', fontWeight: 'bold' }, children: [rotate, "\u00B0"] })] })] }), _jsxs("div", { className: "filter-settings", style: {}, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'flex-end', gap: '10px' }, children: [_jsx("div", { style: { flex: 1 }, children: _jsx(FormAutocomplete, { onChange: (name, value) => {
                                                setFilterSlider(name, value);
                                            }, value: "", label: messages.editor.filter_selection, name: "choise_filter", className: "choose_filter_for_image", options: options.map((option) => {
                                                return { label: option[1], value: option[0] };
                                            }) }) }), _jsx(Button, { variant: "danger", size: "sm", style: { height: 'max-content', padding: '6px 12px', marginBottom: '2px' }, onClick: () => resetFilters(), title: "Filter zur\u00FCcksetzen", children: _jsx("i", { className: "fa-duotone fa-thin fa-delete-left" }) })] }), _jsx("div", { children: renderSlider() })] })] }))] }));
};
export default ImageEditorFTs;
